import { useCallback, useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import {
  FieldErrors,
  SubmitHandler,
  useForm,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useStringMap } from '@/hooks/useStringMap';
import { useAddShipment } from '@/services/shipment-service/hooks';
import { AddShipmentInput } from '@/services/shipment-service/types';
import type { ShippingAccountSearchFragment } from '@/types.gql';

import { BaseShipmentForm } from '../../../shipping-details/components/ShipmentForm/BaseShipmentForm';
import { TOG_SHIPMENT_CATEGORY } from '../../../shipping-details/utils/constants';
import {
  createShipmentSchema,
  ShipmentSchema,
} from '../../../shipping-details/utils/schema';

export const CreateShipmentForm: React.FC = () => {
  const { t } = useTranslation();

  const isShipmentAddressAutoEnabled = useFeatureFlag(
    'Embryo_Shipping_Shipment_address_auto',
  );
  const isShipToAutofillEnabled = useFeatureFlag(
    'Shipping_ShipTo_Address_Autofill',
  );
  const isRefreshBlankScreenEnabled = useFeatureFlag(
    'Shipping_Refresh_BlankScreen',
  );
  const isShippingMethodRequired = useFeatureFlag('Shipping_Method_Required');
  const isShipFromSiteRequired = useFeatureFlag('Shipping_Ship_Site_Required');
  const isCategoryDefaultTogEnabled = useFeatureFlag(
    'Shipping_Category_Default_TOG',
  );

  const shipmentSchema = createShipmentSchema({
    hasCalves: false,
    hasDonors: false,
    hasEmbryos: false,
    hasRecipients: false,
    hasSemen: false,
    enforceContentMatch: false,
    requireShipmentStatus: true,
    requireShippingMethod: isShippingMethodRequired,
    requireShipFromSite: isShipFromSiteRequired,
  });

  const dispatch = useDispatch();
  const { mutateAsync: addShipmentMutation, isPending: isSubmitting } =
    useAddShipment();

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    watch,
    reset,
    resetField,
    getFieldState,
  } = useForm<ShipmentSchema>({
    resolver: zodResolver(shipmentSchema),
    defaultValues: {
      date: new Date().toISOString(),
      needByDate: null,
      originalNeedByDate: null,
      amount: null,
      tank: null,
      trackingNumber: null,
      requestedBy: null,
      phone: null,
      email: null,
      billTo: '',
      billStatus: null,
      comment: null,
      shipName: null,
      shipAddress: null,
      shipAddress2: null,
      shipAddress3: null,
      shipCity: null,
      shipState: null,
      shipZip: null,
      callTag: null,
      shipmentCategory: null,
      shipmentStatus: null,
      shippingMethod: null,
      hasEmbryos: false,
      hasSemen: false,
      hasDonors: false,
      hasRecipients: false,
      hasCalves: false,
      ownerId: null,
      owner: null,
      shipToId: null,
      shipTo: null,
      packedById: null,
      packedBy: null,
      shipFromSiteId: null,
      shipFromSite: null,
    },
  });

  useEffect(() => {
    if (
      isCategoryDefaultTogEnabled &&
      !getFieldState('shipmentCategory').isDirty
    ) {
      resetField('shipmentCategory', { defaultValue: TOG_SHIPMENT_CATEGORY });
    }
  }, [isCategoryDefaultTogEnabled, getFieldState, resetField]);

  const watchedShipmentStatus = useWatch({
    control,
    name: 'shipmentStatus',
  });

  const setShipmentValue: UseFormSetValue<ShipmentSchema> = useCallback(
    (fieldName, fieldValue, options) => {
      setValue(fieldName, fieldValue, options);

      const isAccountField =
        !!fieldValue && typeof fieldValue === 'object' && 'id' in fieldValue;
      if (!isAccountField) {
        return;
      }
      const account = fieldValue as ShippingAccountSearchFragment;
      const ranchAddress = account.ranchAddress;

      if (isShipToAutofillEnabled) {
        if (fieldName !== 'shipTo') {
          return;
        }
        setValue('shipName', account.name ?? null);
        setValue('shipAddress', ranchAddress?.line1 ?? null);
        setValue('shipAddress2', ranchAddress?.line2 ?? null);
        setValue('shipAddress3', ranchAddress?.line3 ?? null);
        setValue('shipCity', ranchAddress?.city ?? null);
        setValue('shipState', ranchAddress?.stateOrProvince ?? null);
        setValue('shipZip', ranchAddress?.postalCode ?? null);
        return;
      }

      if (!isShipmentAddressAutoEnabled) {
        return;
      }

      if (fieldName !== 'owner') {
        return;
      }

      setValue('shipTo', {
        id: account.id,
        name: account.name ?? '',
      });
      setValue('shipToId', account.id);
      setValue('shipName', account.name ?? null);
      setValue('shipAddress', ranchAddress?.line1 ?? null);
      setValue('shipAddress2', ranchAddress?.line2 ?? null);
      setValue('shipAddress3', ranchAddress?.line3 ?? null);
      setValue('shipCity', ranchAddress?.city ?? null);
      setValue('shipState', ranchAddress?.stateOrProvince ?? null);
      setValue('shipZip', ranchAddress?.postalCode ?? null);
    },
    [isShipmentAddressAutoEnabled, isShipToAutofillEnabled, setValue],
  );

  const mapFormDataToAddShipmentInput = useCallback(
    (data: ShipmentSchema): AddShipmentInput => ({
      date: data.date ?? undefined,
      needByDate: data.needByDate ?? undefined,
      originalNeedByDate: data.originalNeedByDate ?? undefined,
      amount: data.amount ?? undefined,
      tank: data.tank ?? undefined,
      trackingNumber: data.trackingNumber ?? undefined,
      requestedBy: data.requestedBy ?? undefined,
      phone: data.phone ?? undefined,
      email: data.email ?? undefined,
      billTo: data.billTo ?? undefined,
      billStatus: data.billStatus ?? undefined,
      comment: data.comment ?? undefined,
      shipName: data.shipName ?? undefined,
      shipAddress: data.shipAddress ?? undefined,
      shipAddress2: data.shipAddress2 ?? undefined,
      shipAddress3: data.shipAddress3 ?? undefined,
      shipCity: data.shipCity ?? undefined,
      shipState: data.shipState ?? undefined,
      shipZip: data.shipZip ?? undefined,
      callTag: data.callTag ?? undefined,
      shipmentCategory: data.shipmentCategory ?? undefined,
      shipmentStatus: data.shipmentStatus ?? undefined,
      shippingMethod: data.shippingMethod ?? undefined,
      hasEmbryos: data.hasEmbryos ?? undefined,
      hasSemen: data.hasSemen ?? undefined,
      hasDonors: data.hasDonors ?? undefined,
      hasRecipients: data.hasRecipients ?? undefined,
      hasCalves: data.hasCalves ?? undefined,
      ownerId: data.ownerId ?? undefined,
      shipToId: data.shipToId ?? undefined,
      packedById: data.packedById ?? undefined,
      shipFromSiteId: data.shipFromSiteId ?? undefined,
    }),
    [],
  );

  const onSubmit = useCallback<SubmitHandler<ShipmentSchema>>(
    async (data) => {
      try {
        const input = mapFormDataToAddShipmentInput(data);
        const result = await addShipmentMutation(input);
        const shipmentId = result.shipment?.id;

        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('shipping.shipmentCreatedSuccessfully'),
            life: ToastLife.MEDIUM,
          }),
        );
        if (isRefreshBlankScreenEnabled && shipmentId) {
          window.open(`/shipping/shipment?id=${shipmentId}`, '_blank');
        }
        reset();
      } catch {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail: t('shipping.failedToCreateShipment'),
            life: ToastLife.MEDIUM,
          }),
        );
      }
    },
    [
      addShipmentMutation,
      dispatch,
      isRefreshBlankScreenEnabled,
      mapFormDataToAddShipmentInput,
      reset,
      t,
    ],
  );

  const onInvalid = useCallback(
    (_errors: FieldErrors<ShipmentSchema>) => {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.validationError'),
          detail: t('common.pleaseFixErrors'),
          life: ToastLife.MEDIUM,
        }),
      );
    },
    [dispatch, t],
  );

  const shipmentStatusResult = useStringMap('shipment_status');
  const shipmentStatus = useMemo(
    () => ({
      options:
        shipmentStatusResult.type === 'success'
          ? (shipmentStatusResult.options ?? [])
          : [],
    }),
    [shipmentStatusResult],
  );

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button
          type="button"
          icon="pi pi-plus"
          onClick={handleSubmit(onSubmit, onInvalid)}
          disabled={
            !formState.isDirty || isSubmitting || watchedShipmentStatus == null
          }
          loading={isSubmitting}
          label={t('shipping.createShipment')}
        />
      </div>

      <BaseShipmentForm
        control={control}
        handleSubmit={handleSubmit}
        setValue={setShipmentValue}
        watch={watch}
        onSubmit={onSubmit}
        shipmentStatus={shipmentStatus}
      />
    </>
  );
};
